import { useEffect, useState } from "react";
import { NMAxiosInst } from "@neumetric/blueprint-lib/store";
import { NMStatsTable, BarChart, DonutChart, Col } from "@neumetric/blueprint-lib/components";
import { ExportSection } from "../ExportSection";

const MODULE_KEY = "Continuer_Continuity_Test_Status";

const Columns = [
  {
    title: "",
    attribute: "department",
    width: "16%",
  },
  {
    title: "OVERALL",
    attribute: "total",
    id: "Overall",
    highlight: { header: true, cell: true },
    overallFlag: true,
  },
  {
    title: "Closed",
    attribute: "closed",
    id: "CLOSED",
  },
  {
    title: "Started",
    attribute: "inprogress",
    id: "STARTED",
  },
  { title: "Open", attribute: "open", id: "PENDING" },
  {
    title: "Other",
    attribute: "other",
    id: "INVALID",
  },
];

export function ContinuerStats({ departments, user, dateRange, dateRangeList, mode }) {
  const initialRows = departments.map((deptObj) => {
    return {
      department: deptObj.id,
      total: 0,
      closed: 0,
      inprogress: 0,
      open: 0,
      minimal: 0,
      other: 0,
    };
  });

  const dateRangeEnums = dateRangeList || [];

  const effectiveRangeId = typeof dateRange === "string" ? dateRange : dateRange?.id;

  const [state, setState] = useState([...initialRows]);

  const getDataFromServer = async (dateRange) => {
    let payload = {
      date_range_type: "last_365_days",
    };
    if (dateRange && dateRange.id) {
      payload.date_range_type = dateRange.id;
    }
    let res = await NMAxiosInst.post("/continuers/department_dashboard", payload);

    // --- If no data at all, show blank ---
    if (!res || !res.department_status || Object.keys(res.department_status).length === 0) {
      setState([]); // blank -> "No Data To Display!"
      return;
    }
    if (res.department_status) {
      const brandname = user.org.brandname;

      // Normalize: ensure every department is present
      let rows = departments.map((deptObj) => {
        const deptRow = res.department_status[deptObj.id]?.statuses || {};
        const total = deptRow.total || 0;
        const closed = deptRow.closed || 0;
        const inprogress = deptRow.inprogress || 0;
        const open = deptRow.open || 0;
        const other = total - (closed + inprogress + open);

        return {
          department: deptObj.id,
          total,
          closed,
          inprogress,
          open,
          other,
        };
      });
      // Build total row
      const totalRow = rows.reduce(
        (acc, r) => ({
          ...acc,
          total: acc.total + (Number(r.total) || 0),
          closed: acc.closed + (Number(r.closed) || 0),
          inprogress: acc.inprogress + (Number(r.inprogress) || 0),
          open: acc.open + (Number(r.open) || 0),
          other: acc.other + (Number(r.other) || 0),
        }),
        {
          department: brandname,
          total: 0,
          closed: 0,
          inprogress: 0,
          open: 0,
          other: 0,
          highlight: true,
        }
      );

      // Sort alphabetically and prepend total row
      rows.sort((a, b) => a.department.toLowerCase().localeCompare(b.department.toLowerCase()));
      rows = [totalRow, ...rows];

      // Replace numeric 0s with '-' (but leave totals intact if you want raw numbers there)
      rows.forEach((row) => {
        Object.keys(row).forEach((columnKey) => {
          if (
            columnKey !== "department" &&
            row[columnKey] !== null &&
            row[columnKey] !== undefined &&
            (row[columnKey] === 0 || row[columnKey] === "0")
          ) {
            row[columnKey] = "-";
          }
        });
      });

      setState(rows);
    }
  };

  useEffect(() => {
    if (dateRange) {
      getDataFromServer(dateRange);
    }
  }, [dateRange]);

  const toNum = (v) => (v === "-" || v == null ? 0 : Number(v));

  // rows excluding the brand/total row
  const rows = (state || []).filter((r) => r && r.department && r.department !== user.org.brandname);

  // which depts actually have any non-zero values?
  const deptsWithData = rows
    .filter((r) => toNum(r.closed) + toNum(r.inprogress) + toNum(r.open) + toNum(r.other) > 0)
    .map((r) => r.department);

  // fixed series order to keep colors consistent
  const SERIES_ORDER = ["OTHER", "OPEN", "STARTED", "CLOSED"];

  // map display label -> blueprint color key
  const COLOR_KEY = {
    OTHER: "INVALID",
    OPEN: "PENDING",
    STARTED: "STARTED",
    CLOSED: "COMPLETE",
  };

  // pretty labels for legend (if your BarChart uses them)
  const LABEL_TEXT = {
    OTHER: "Other",
    OPEN: "Open",
    STARTED: "Started",
    CLOSED: "Closed",
  };

  const barChartData = Object.fromEntries(
    (departments || [])
      .map((dept) => dept.id)
      .filter((name) => name && name !== user.org.brandname)
      .sort((a, b) => a.localeCompare(b))
      .map((dept) => {
        const r = (state || []).find((x) => x.department === dept) || {};
        const values = {
          OTHER: toNum(r.other),
          OPEN: toNum(r.open),
          STARTED: toNum(r.inprogress),
          CLOSED: toNum(r.closed),
        };
        return [
          dept,
          SERIES_ORDER.map((s) => ({
            key: COLOR_KEY[s],
            label: LABEL_TEXT[s],
            value: values[s], // always numeric
          })),
        ];
      })
  );
  const dateRangeLabel =
    dateRange?.label ??
    dateRangeEnums.find((o) => o.id === (typeof dateRange === "string" ? dateRange : dateRange?.id))?.label ??
    "";

  useEffect(() => {
    if (!effectiveRangeId) return;
    getDataFromServer({ id: effectiveRangeId }); // normalize payload to {id}
  }, [effectiveRangeId]);

  // ---- Donut (overall) numbers for Continuity ----
  const safeNum = (v) => (v === "-" || v == null ? 0 : Number(v));

  // find the brand/overall row (it’s the one you unshift with brandname)
  const brandRow = state?.find((r) => r.department === user.org.brandname) ?? null;

  const totalTests = brandRow ? safeNum(brandRow.total) : 0;
  const otherTests = brandRow ? safeNum(brandRow.other) : 0;
  const completeTests = brandRow ? safeNum(brandRow.closed) : 0;

  const effectiveTotal = totalTests - otherTests;

  // Completion = Complete / (Total - Other)
  const completionPct = brandRow && effectiveTotal > 0 ? Math.round((completeTests * 100) / effectiveTotal) : 0;
  // Order chosen to match your screenshot layout: Other → Started → Open → Complete
  const donutDatasets = [
    {
      key: "COMPLETE",
      value: brandRow ? safeNum(brandRow.closed) : 0,
      label: "Closed",
      sortOrder: 4,
      legendOrder: 1,
      textColor: "#FFF",
    },
    {
      key: "STARTED",
      value: brandRow ? safeNum(brandRow.inprogress) : 0,
      label: "Started",
      border: "true",
      sortOrder: 2,
      legendOrder: 2,
    },
    {
      key: "OPEN",
      value: brandRow ? safeNum(brandRow.open) : 0,
      label: "Open",
      sortOrder: 3,
      legendOrder: 3,
      textColor: "#FFF",
    },
    {
      key: "OTHER",
      value: brandRow ? safeNum(brandRow.other) : 0,
      label: "Other",
      border: "true",
      sortOrder: 1,
      legendOrder: 4,
    },
  ];
  const donutDatasetToTable = (allDonutSets = []) => {
    const totals = {
      COMPLETE: 0,
      STARTED: 0,
      OPEN: 0,
      OTHER: 0,
    };

    allDonutSets.forEach((d) => {
      const key = d.key?.toUpperCase();
      if (key in totals) totals[key] += Number(d.value) || 0;
    });

    const totalTasks = totals.COMPLETE + totals.STARTED + totals.OPEN + totals.OTHER;
    const completion = totalTasks > 0 ? Math.round((totals.COMPLETE / (totalTasks - totals.OTHER)) * 100) + "%" : "-";

    const columns = [
      { title: "Closed", attribute: "COMPLETE" },
      { title: "Started", attribute: "STARTED" },
      { title: "Open", attribute: "OPEN" },
      { title: "Other", attribute: "OTHER" },
      { title: "Completion (%)", attribute: "COMPLETION" },
    ];

    const Rows = [
      {
        COMPLETE: totals.COMPLETE,
        STARTED: totals.STARTED,
        OPEN: totals.OPEN,
        OTHER: totals.OTHER,
        COMPLETION: completion,
      },
    ];

    return { columns, Rows };
  };
  const { columns, Rows } = donutDatasetToTable(donutDatasets);

  return (
    <div className="o-auto">
      {/* ---- NON-FUSION MODE (table + chart) ---- */}
      {(mode === "export_everything" || mode === "full") && (
        <>
          <div
            style={{
              display: "flex",
              width: "100%",
              gap: "20px",
              alignItems: "flex-start",
              flexWrap: "wrap",
              padding: "20px 40px 40px 40px",
            }}
          >
            {/* TABLE SECTION */}
            <ExportSection
              moduleKey={MODULE_KEY}
              index={1}
              dateRangeLabel={dateRange?.label}
              exportType="table"
              exportData={{
                title: "Continuity - Department-wise",
                columns: Columns,
                rows: state,
              }}
              style={{
                flex: "1 1 48%",
                minWidth: 400,
                maxWidth: "50%",
              }}
            >
              <NMStatsTable
                id={MODULE_KEY}
                title="Continuity"
                subTitle="Department-wise"
                columns={Columns}
                dataSource={state}
                dateRange={dateRangeLabel}
                showDateInHeader={true}
                getCellTypeRepresentation={(cellInfo) => {
                  if (cellInfo.rowId === -1 && cellInfo.columnId !== "overall") {
                    return "DOT";
                  }
                  return null;
                }}
              />
            </ExportSection>

            {/* BAR CHART SECTION */}
            <ExportSection
              moduleKey={MODULE_KEY}
              index={2}
              dateRangeLabel={dateRange?.label}
              exportType="chart"
              style={{
                flex: "1 1 48%",
                minWidth: 400,
                maxWidth: "50%",
                paddingTop: "10px",
                overflow: "visible",
              }}
            >
              <div className="chart-export-pad" style={{ width: "100%", overflow: "visible" }}>
                <div style={{ width: "100%", height: 500 }}>
                  <BarChart
                    id={MODULE_KEY}
                    title="Continuity"
                    subtitle="Department-wise"
                    datasets={barChartData}
                    label={{ xAxis: "Tests", yAxis: "Department" }}
                    stackType="normal"
                    dateRange={dateRangeLabel}
                    showDateInHeader={true}
                    classes={{ root: "h-500-px w-100" }}
                  />
                </div>
              </div>
            </ExportSection>
          </div>
          <div style={{ paddingTop: "100px" }}>
            <hr className="nm-hr nm-hr--center" />
          </div>
        </>
      )}

      {/* ---- FUSION MODE (donut only) ---- */}
      {(mode === "export_everything" || mode === "fusion") && (
        <div
          style={{
            display: "flex",
            width: "100%",
            alignItems: "flex-start",
            flexWrap: "wrap",
            padding: "20px 0 20px 10px",
          }}
        >
          <ExportSection
            moduleKey={MODULE_KEY}
            index={mode === "export_everything" ? 3 : 1}
            exportType={mode === "export_everything" ? "chart" : "table"}
            exportData={{
              title: "Continuity",
              columns: columns,
              rows: Rows,
            }}
            style={{
              display: "inline-block",
              width: 350,
              flex: "0 0 auto",
            }}
          >
            <Col style={{ flex: "1 1 0", width: 350 }}>
              <div style={{ width: "100%", height: 200 }}>
                <DonutChart
                  id={MODULE_KEY}
                  chartId="continuer-overall-donut"
                  title="Continuity"
                  subtitle="Tests:"
                  totalCount={String(totalTests || "-")}
                  showDateInHeader={false}
                  classes={{ root: "h-200-px" }}
                  datasets={donutDatasets}
                  metric={{
                    label: "Completion",
                    value: completionPct,
                    type: "percent",
                  }}
                />
              </div>
            </Col>
          </ExportSection>
        </div>
      )}
    </div>
  );
}
